'use client';

import CardTimeTurn from './CardTimeTurn';
import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel';
import { TypeCarousel_time_turn } from './types/AdmissionType';
import { Ref, useEffect, useImperativeHandle, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { axiosClientInstance } from '@/lib/servies/axiosConfig';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { TypeTimeTurn } from './GridTimeTurn';

export type DaysGrid = {
    setSelectedDay: (index: number, time: number | null | 'first') => void;
};

export default function Carousel_time_turn({
    daysRef,
    ...props
}: TypeCarousel_time_turn & { daysRef: Ref<DaysGrid> }) {
    const [api, setApi] = useState<CarouselApi>();
    const [turnTimeDay, setTurnTimeDay] = useState<{
        day: number | null;
        turn: number | null | 'first';
    }>({ day: null, turn: null });
    const [shouldSelectFirst, setShouldSelectFirst] = useState(true);

    const {
        currentDateTimes,
        currentDates,
        doctor,
        handleSetTimes,
        setSelectedTurn,
        matab_plus_id,
        firstDateTimes,
    } = props;

    useImperativeHandle(daysRef, () => ({
        setSelectedDay: (index, time) => {
            setTurnTimeDay({ day: index, turn: time });

            if (turnTimeDay.day === index) {
                setShouldSelectFirst(true);
            }

            setSelectedTurn(time);
        },
    }));

    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            const bodyData = {
                doctor_id: Number(doctor.docid),
                clinic_id: Number(doctor.clinic),
                date_g: currentDates[turnTimeDay.day!].date_g,
                plus_id: matab_plus_id ?? 0,
            };
            const url =
                matab_plus_id && matab_plus_id !== 0
                    ? '/get_day_turns_plus'
                    : '/day_turns';
            return axiosClientInstance.post(url, bodyData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
        },
        onSuccess: (res) => {
            handleSetTimes(
                turnTimeDay.day!,
                (res?.data.result?.turns_day as never) || [],
            );
        },
        onError: () => {
            setTurnTimeDay((prev) => ({ ...prev, turn: null }));
        },
        onSettled: (data) => {
            const turns = (data?.data.result?.turns_day ??
                []) as TypeTimeTurn[];
            if (turnTimeDay.turn === 'first') {
                let time = 0;
                if (turns.length <= 0) {
                    setSelectedTurn(null);
                }

                while (
                    time <= turns.length &&
                    turns[time] &&
                    turns[time].status === 'close'
                ) {
                    time++;
                }

                if (time > turns.length) {
                    setSelectedTurn(null);
                } else {
                    setSelectedTurn(time);
                }
            } else {
                setSelectedTurn(null);
            }
        },
        retry: 3,
        retryDelay: 2000,
    });

    useEffect(() => {
        if (shouldSelectFirst) {
            let time = 0;
            if (currentDateTimes.length <= 0) {
                setSelectedTurn(null);
            }

            while (
                time <= currentDateTimes.length &&
                currentDateTimes[time] &&
                currentDateTimes[time].status === 'close'
            ) {
                time++;
            }

            if (time > currentDateTimes.length) {
                setSelectedTurn(null);
            } else {
                setSelectedTurn(time);
            }

            setShouldSelectFirst(false);
        }
    }, [currentDateTimes, setSelectedTurn, shouldSelectFirst]);

    useEffect(() => {
        api?.scrollTo(0, true);
    }, [api]);

    useEffect(() => {
        if (turnTimeDay.day === null) return;

        mutate();
        api?.scrollTo(turnTimeDay.day, true);
    }, [turnTimeDay.day, mutate, api, firstDateTimes, turnTimeDay.turn]);

    function handleSetTurnTimeDay(value: number | null) {
        if (value === turnTimeDay.day) return;

        setSelectedTurn(null);
        setTurnTimeDay({ day: value, turn: null });
    }

    useEffect(() => {
        if (!firstDateTimes) {
            setTurnTimeDay({ day: turnTimeDay.day ?? 0, turn: null });
        }
    }, [currentDates, firstDateTimes, turnTimeDay.day]);

    return (
        <div className="flex gap-3 w-full">
            <Carousel
                opts={{ direction: 'rtl' }}
                setApi={setApi}
                className="!w-[95%] mx-auto"
            >
                <CarouselContent className="py-4">
                    {currentDates.map((item, i) => (
                        <CarouselItem key={i} style={{ flexBasis: 'auto' }}>
                            <CardTimeTurn
                                data={item}
                                funHnaleder={handleSetTurnTimeDay}
                                currentValue={turnTimeDay.day}
                                value={i}
                                loading={isPending}
                            />
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <CarouselPrevious
                    icon={<FaAngleLeft />}
                    className="!p-[1px] !text-white !mr-[1px] !bg-blue hover:!text-primary-48 border-white !size-[2rem] text-xl -left-3 disabled:!hidden"
                    invert
                />
                <CarouselNext
                    icon={<FaAngleRight />}
                    className="!p-[1px] !text-white !bg-blue hover:!text-primary-48 border-white !size-[2rem] text-xl -right-3 disabled:!hidden"
                    invert
                />
            </Carousel>
        </div>
    );
}
